import { translate } from '@/shared/i18n/instance';

/**
 * Erreur d'API telle que la voit l'interface.
 *
 * Le backend répond toujours `{ statusCode, error, message, path, timestamp }`,
 * `message` étant une chaine ou la liste des erreurs de validation. On aplatit
 * ici en un texte affichable, une fois pour toutes : aucun écran ne manipule la
 * forme brute.
 *
 * **La langue.** Le serveur écrit ses refus en français, et lui seul. Trois
 * niveaux, du plus précis au plus général :
 *
 *   1. un **code** de refus connu (`PHONE_TAKEN`…) : on affiche notre phrase,
 *      dans la langue en cours : c'est le cas des refus que l'utilisateur
 *      rencontre vraiment, ceux d'un formulaire ;
 *   2. sinon la phrase du serveur, telle quelle : elle est précise, et perdre
 *      cette précision serait payer la traduction trop cher ;
 *   3. sinon, faute de tout, le repli du code HTTP, traduit.
 *
 * On ne traduit jamais la phrase du serveur au caractère près : elle changera,
 * et une comparaison de texte casserait sans bruit.
 */
export class ApiError extends Error {
  readonly status: number;
  /** Détail par champ, quand le serveur a renvoyé des erreurs de validation. */
  readonly details: Array<string>;
  /**
   * Code du refus, quand le serveur en a posé un (`PHONE_TAKEN`,
   * `EMAIL_TAKEN`…). Il dit de quoi parle le message : l'écran s'en sert pour
   * l'afficher sous le bon champ et proposer le bon raccourci.
   */
  readonly code: string | null;

  constructor(
    status: number,
    message: string,
    details: Array<string> = [],
    code: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
    this.code = code;
  }

  /** Le compte n'a pas le droit d'agir : l'écran propose autre chose, il n'insiste pas. */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /** Session finie : la garde de navigation renvoie a l'accueil public. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** Conflit métier (déja fait, déja pris) ou transition impossible. */
  get isConflict(): boolean {
    return this.status === 409 || this.status === 422;
  }
}

/**
 * Les codes HTTP dont on sait dire quelque chose d'utile, et leur clé.
 *
 * Écrits un a un plutot que composés en `error.status.${status}` : le
 * compilateur vérifie alors que chaque clé existe vraiment dans les
 * traductions, et un repli oublié se voit a la compilation.
 */
const STATUS_KEYS = {
  400: 'error.status.400',
  401: 'error.status.401',
  403: 'error.status.403',
  404: 'error.status.404',
  409: 'error.status.409',
  422: 'error.status.422',
  429: 'error.status.429',
  500: 'error.status.500',
} as const;

/** Ce qu'on dit d'un code HTTP quand le serveur n'a rien dit de mieux. */
function statusMessage(status: number): string {
  return translate(STATUS_KEYS[status as keyof typeof STATUS_KEYS] ?? STATUS_KEYS[500]);
}

/** Les refus que le serveur nomme, et que l'application sait donc redire. */
const CODE_KEYS = {
  PHONE_TAKEN: 'error.code.PHONE_TAKEN',
  PHONE_UNAVAILABLE: 'error.code.PHONE_UNAVAILABLE',
  EMAIL_TAKEN: 'error.code.EMAIL_TAKEN',
  EMAIL_TAKEN_GOOGLE: 'error.code.EMAIL_TAKEN_GOOGLE',
  // Le code de confidentialité. Les refus qui portent un nombre (essais
  // restants, minutes de blocage) sont rédigés par l'écran de saisie, qui lit
  // l'état du code : ici, la phrase générale.
  PIN_REQUIRED: 'error.code.PIN_REQUIRED',
  PIN_INVALID: 'error.code.PIN_INVALID',
  PIN_LOCKED: 'error.code.PIN_LOCKED',
  PIN_NOT_SET: 'error.code.PIN_NOT_SET',
  PIN_TOO_SIMPLE: 'error.code.PIN_TOO_SIMPLE',
  PIN_CURRENT_REQUIRED: 'error.code.PIN_CURRENT_REQUIRED',
} as const;

/**
 * Notre phrase pour un code de refus connu, ou `null` : un code inconnu laisse
 * la main au message du serveur, qui en dira toujours plus qu'un repli.
 */
function codeMessage(code: string | null): string | null {
  const key = code === null ? undefined : CODE_KEYS[code as keyof typeof CODE_KEYS];
  return key === undefined ? null : translate(key);
}

interface ServerErrorBody {
  statusCode?: number;
  error?: string;
  message?: string | Array<string>;
}

/**
 * Un code de refus s'écrit en capitales (`PHONE_TAKEN`). Le meme champ porte
 * sinon le libellé HTTP posé par le serveur (« Conflict », « Bad Request ») :
 * ce n'est pas un code, et aucun écran ne doit croire en reconnaitre un.
 */
const CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/;

/** Construit l'erreur affichable a partir de la réponse du serveur. */
export function toApiError(status: number, body: unknown): ApiError {
  const parsed = (body ?? {}) as ServerErrorBody;
  const raw = parsed.message;
  const code =
    typeof parsed.error === 'string' && CODE_PATTERN.test(parsed.error) ? parsed.error : null;
  const known = codeMessage(code);

  if (Array.isArray(raw) && raw.length > 0) {
    return new ApiError(status, known ?? raw[0], raw, code);
  }
  if (typeof raw === 'string' && raw.length > 0) {
    return new ApiError(status, known ?? raw, [], code);
  }
  return new ApiError(status, known ?? statusMessage(status), [], code);
}

/**
 * Panne réseau : le serveur n'a rien répondu du tout.
 *
 * L'exception d'origine reste attachée (`cause`) sans être montrée : une
 * requête qui ne part même pas, faute de savoir construire son corps, se
 * présentait sinon exactement comme une coupure, et c'est ce qui a masqué
 * l'envoi de photos cassé par le passage à `expo/fetch`.
 */
export function networkError(cause?: unknown): ApiError {
  const error = new ApiError(0, translate('error.network'));
  error.cause = cause;
  return error;
}

/** Message a afficher pour n'importe quelle erreur remontée par React Query. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return statusMessage(500);
}

/**
 * Code du refus, s'il y en a un. Une panne réseau ou une erreur inattendue n'en
 * portent pas : l'écran retombe alors sur son affichage général.
 */
export function errorCode(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null;
}
